/**
 * Civil-date period presets (TMMRSL-0018). Arithmetic is in local calendar terms —
 * never `toISOString().slice(0, 10)`, which shifts the civil day west of Greenwich.
 */

export type PeriodPreset = 'current' | 'last30' | 'last90' | 'thisMonth' | 'lastMonth' | 'custom';

export type ComputedPreset = Exclude<PeriodPreset, 'current' | 'custom'>;

export type PeriodRange = { from?: string; to?: string };

export type StoredPeriod =
  | { kind: 'preset'; id: Exclude<PeriodPreset, 'custom'> }
  | { kind: 'custom'; from: string; to: string };

const PRESET_IDS: ReadonlySet<string> = new Set(['current', 'last30', 'last90', 'thisMonth', 'lastMonth']);

const CIVIL = /^(\d{4})-(\d{2})-(\d{2})$/;

export type PeriodScope = 'reseller' | 'tenant';

/**
 * Where a screen's period choice persists (H §9.4): reseller-wide screens under
 * `tmm_reseller_admin.period.<screen>`, per-tenant screens under
 * `tmm_reseller_admin.period.tenant.<screen>` (the tenant workspace, TMMRSL-0029).
 */
export function periodStorageKey(screen: string, scope: PeriodScope = 'reseller'): string {
  return scope === 'tenant'
    ? `tmm_reseller_admin.period.tenant.${screen}`
    : `tmm_reseller_admin.period.${screen}`;
}

/** Today's civil date (`YYYY-MM-DD`) in the local calendar. */
export function todayCivil(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isCivilDate(value: string): boolean {
  const match = CIVIL.exec(value);
  if (match === null) return false;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

function padCivil(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function addDays(civil: string, delta: number): string {
  const match = CIVIL.exec(civil);
  if (match === null) throw new Error(`Not a civil date (YYYY-MM-DD): ${civil}`);
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + delta);
  return todayCivil(date);
}

/** Inclusive ranges: last30 = today−29 … today; last90 likewise. */
export function presetRange(preset: ComputedPreset, today: string): { from: string; to: string } {
  if (!isCivilDate(today)) throw new Error(`Not a civil date (YYYY-MM-DD): ${today}`);
  const match = CIVIL.exec(today);
  if (match === null) throw new Error(`Not a civil date (YYYY-MM-DD): ${today}`);
  const y = Number(match[1]);
  const m = Number(match[2]);
  switch (preset) {
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'last90':
      return { from: addDays(today, -89), to: today };
    case 'thisMonth':
      return { from: padCivil(y, m, 1), to: today };
    case 'lastMonth': {
      const lastPrev = new Date(y, m - 1, 0);
      const py = lastPrev.getFullYear();
      const pm = lastPrev.getMonth() + 1;
      return { from: padCivil(py, pm, 1), to: padCivil(py, pm, lastPrev.getDate()) };
    }
    default: {
      const _exhaustive: never = preset;
      throw new Error(`Unknown period preset: ${_exhaustive}`);
    }
  }
}

export function serialize(stored: StoredPeriod): string {
  if (stored.kind === 'preset') return stored.id;
  return JSON.stringify({ from: stored.from, to: stored.to });
}

export function parseStored(raw: string | null): StoredPeriod | null {
  if (!raw) return null;
  if (PRESET_IDS.has(raw)) {
    return { kind: 'preset', id: raw as Exclude<PeriodPreset, 'custom'> };
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
    const from = 'from' in value ? value.from : undefined;
    const to = 'to' in value ? value.to : undefined;
    if (typeof from === 'string' && typeof to === 'string' && isCivilDate(from) && isCivilDate(to)) {
      return { kind: 'custom', from, to };
    }
  } catch {
    return null;
  }
  return null;
}

export function rangeForStored(stored: StoredPeriod | null, today: string): PeriodRange {
  if (!stored) return {};
  if (stored.kind === 'custom') return { from: stored.from, to: stored.to };
  if (stored.id === 'current') return {};
  return presetRange(stored.id, today);
}

/** A civil month, `YYYY-MM`. */
export function isCivilMonth(value: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(value);
}

/**
 * The `count` months up to and including today's, newest first, as `YYYY-MM` (TMMRSL-0073: the
 * Statements period filter). Local calendar arithmetic, like the presets.
 */
export function recentMonths(count: number, today: string): string[] {
  const match = CIVIL.exec(today);
  if (match === null) throw new Error(`Not a civil date (YYYY-MM-DD): ${today}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  return Array.from({ length: count }, (_, back) => {
    const date = new Date(year, month - 1 - back, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  });
}

export function isValidCustomRange(from: string, to: string, today: string): boolean {
  return isCivilDate(from) && isCivilDate(to) && from <= to && to <= today;
}
